import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import NumberFlow from '@number-flow/react'

import { currencyFormatOptions, formatCurrency } from '@/utils/currency'

import Header from '@/components/dashboard/Header/Header'
import Navigation from '@/components/dashboard/Navigation/Navigation'
import MotionDiv from '@/components/common/Motion/MotionDiv'

import ExpensesCategoryGrid from '@/components/pages/Expenses/ExpensesCategoryGrid'
import ExpensesEmptyState from '@/components/pages/Expenses/ExpensesEmptyState'
import DriveWidget from '@/components/pages/Expenses/Drive/DriveWidget'
import DriveSheets from '@/components/pages/Expenses/Drive/DriveSheets'
import DriveItemChip from '@/components/pages/Expenses/Drive/DriveItemChip'
import { driveCopyState, pendingCopyCount } from '@/components/pages/Expenses/Drive/driveCopyState'
import { useUserStore } from '@/store/useUserStore'

import MotionList, {
  formatDateGroup,
} from '@/components/common/Motion/MotionList'

import expenseAPI, { ExpenseItem } from '@/services/api/expense'

import { useDashboardStore } from '@/store/useDashboardStore'
import { useTourStore } from '@/store/useTourStore'
import { useExpensesFormStore } from '@/store/useExpensesFormStore'

import { expenseIconMap } from '../Expenses/ExpensesMemoPage'
import { ExpenseCategory } from '@/types/expense'
import { usePageSetup } from '@/hooks/usePageSetup'

import ExpensesTour from '@/tours/ExpensesTour'
import useDeleteMonth from '@/components/dashboard/DeleteMonthModal/useDeleteMonth'

const expenseSubtitle = (expense: ExpenseItem) => {
  return expense.operation || expense.date
}

const expenseIcon = (expense: ExpenseItem) => {
  return expenseIconMap[expense.category]
}

// TODO: “Įkėlėte degalų išlaidą ir sutaupėte estimate 1 eurą “
// TODO: Vėliau būtų galima siųsti ir mėnesinius stats, kur rodytų kiek buvo įkelta income, expense ir koks total estimated savings

export const DashboardExpensesPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  usePageSetup(t('expenses.pageTitle.list'))
  const setExpensesForm = useExpensesFormStore((state) => state.setForm)

  const financialYear = useDashboardStore((state) => state.financialYear)

  const expenses = useDashboardStore((state) => state.expenses)
  const setExpenses = useDashboardStore((state) => state.setExpenses)

  const driveStatus = useUserStore((state) => state.user?.google_drive_connection?.status ?? 'disconnected')

  const loadExpenses = () => expenseAPI.index({ year: financialYear })
    .then((response) => setExpenses(response.expenses))

  useEffect(() => { loadExpenses() }, [])

  useEffect(() => {
    if (pendingCopyCount(expenses) === 0) return

    const timer = setTimeout(loadExpenses, 5000)
    return () => clearTimeout(timer)
  }, [expenses])

  const { modal: DeleteMonthModal, requestMonthDelete } = useDeleteMonth('expenses')

  const expensesTourCompleted = useTourStore((state) => state.expensesTourCompleted)

  useEffect(() => {
    if (!ExpensesTour.isActive() && !expensesTourCompleted) {
      ExpensesTour.start()
    }
  }, [expenses, expensesTourCompleted])

  const total = expenses.reduce((acc, expense) => acc + expense.amount, 0)

  const items = expenses.map((expense) => ({
    id: expense.updated_at + expense.amount,
    title: "",
    subtitle: expenseSubtitle(expense),
    value: formatCurrency.format(expense.amount),
    badge: expense.google_drive_status
      ? <DriveItemChip status={expense.google_drive_status} />
      : undefined,
    icon: expenseIcon(expense),
    groupId: formatDateGroup(expense.date, i18n.language),
    props: {
      onClick: async () => {
        await setExpensesForm({
          category: expense.category as ExpenseCategory,
          operation: expense.operation,
          total: expense.original_amount,
          percentage: expense.percentage,
          date: expense.date,
          reference: expense.reference,
          sgid: expense.sgid.global
        })
        navigate(`/expenses/update`)
      }
    }
  }))

  /* TODO: Refactor to one function for expenses and incomes */
  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const isCurrentYear = currentYear === financialYear

  const groups = Array.from({ length: 12 }).map((_, index) => {
    const monthIndex = 11 - index
    const monthDate = new Date(financialYear, monthIndex, 1);
    const monthName = formatDateGroup(monthDate.toISOString(), i18n.language);

    const groupItems = expenses.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate.getMonth() === monthIndex;
    });

    if (
      groupItems.length === 0 &&
      isCurrentYear &&
      monthIndex > currentMonthIndex
    ) {
      return null
    }

    const groupTotal = groupItems.reduce((sum, item) => sum + item.amount, 0);
    const monthCopyState = driveCopyState(groupItems)

    return {
      id: monthName,
      name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      description: groupTotal > 0 ? formatCurrency.format(groupTotal) : '',
      badge: monthCopyState
        ? <DriveItemChip status={monthCopyState} interactive={false} showLabel={false} />
        : undefined,
      open: (11 - index) == currentMonthIndex && isCurrentYear,
      friction: 0.005,
      childrenFriction: 0.01,
      EmptyGroup: <ExpensesEmptyState />,
      onDelete: groupItems.length > 0
        ? () => requestMonthDelete({
          monthIndex,
          name: monthName.charAt(0).toUpperCase() + monthName.slice(1)
        })
        : undefined,
      props: {
        className: 'border-b border-zinc-200 pb-4'
      }
    }
  }).filter((group) => group !== null);

  return (
    <div className="h-full bg-white flex flex-col">
      {DeleteMonthModal}
      <DriveSheets copyState={driveCopyState(expenses)} />
      <Header />
      <div className="flex-1 p-5 pt-2 pb-[10rem]">
        <MotionDiv className='mb-5'>
          <p className='text-zinc-700 text-sm'>{t('dashboard.yourYearlyExpenses')}</p>
          <NumberFlow
            format={{
              style: currencyFormatOptions.style,
              currency: currencyFormatOptions.currency,
              useGrouping: currencyFormatOptions.useGrouping
            }}
            className='text-[1.75rem] font-semibold text-zinc-900'
            value={total}
          />
        </MotionDiv>
        <MotionDiv className='mb-6'>
          <ExpensesCategoryGrid />
        </MotionDiv>
        <DriveWidget
          connectionStatus={driveStatus}
          copyState={driveCopyState(expenses)}
          pendingCount={pendingCopyCount(expenses)}
        />
        <MotionList
          items={items}
          groups={groups}
          className='tour-expense-list'
        />
      </div>
      <Navigation />
    </div>
  )
}

export default DashboardExpensesPage